import { isObject, zipObject } from 'lodash-es';
import type { Record as BoltRecord } from 'neo4j-driver';
import neo4j from 'neo4j-driver';
import type { Integer, Node, Path, PathSegment, Record, RecordShape, Relationship } from 'neo4j-driver-core';
import { isInt, isNode, isPath, isPathSegment, isRelationship } from 'neo4j-driver-core';

import type { Nullable } from '../../types/utility';
import { isFalsy } from '../../types/utility';

export const extractNodesAndRelationshipsFromRecords = (
  records?: Nullable<BoltRecord[]>,
): {
  nodes: Node[];
  relationships: Relationship[];
} => {
  const nodeMap = new Map<string, Node>();
  const relMap = new Map<string, Relationship>();

  if (isFalsy(records) || records.length === 0 || isFalsy(records[0])) {
    return { nodes: [], relationships: [] };
  }

  const addNode = (node: Node) => {
    if (!nodeMap.has(node.elementId)) {
      nodeMap.set(node.elementId, node);
    }
  };

  const addRelationship = (relationship: Relationship) => {
    if (!relMap.has(relationship.elementId)) {
      relMap.set(relationship.elementId, relationship);
    }
  };

  for (const record of records) {
    for (const value of record.values()) {
      extractNodesAndRelationshipsFromValue(value, addNode, addRelationship);
    }
  }

  return { nodes: Array.from(nodeMap.values()), relationships: Array.from(relMap.values()) };
};

const extractNodesAndRelationshipsFromValue = (
  value: unknown,
  addNode: (node: Node) => void,
  addRelationship: (relationship: Relationship) => void,
) => {
  if (isNode(value)) {
    addNode(value);
  } else if (isRelationship(value)) {
    addRelationship(value);
  } else if (Array.isArray(value)) {
    value.forEach((item) => extractNodesAndRelationshipsFromValue(item, addNode, addRelationship));
  } else if (isPath(value)) {
    const path = value;
    if (path.segments.length < 1) {
      if (path.start) {
        addNode(path.start);
      }
    } else {
      extractNodesAndRelationshipsFromValue(path.segments, addNode, addRelationship);
    }
  } else if (isPathSegment(value)) {
    const segment = value;
    if (segment.start) {
      addNode(segment.start);
    }
    if (segment.end) {
      addNode(segment.end);
    }
    if (segment.relationship) {
      addRelationship(segment.relationship);
    }
  } else if (isObject(value) && !isInt(value)) {
    Object.values(value).forEach((item) => {
      extractNodesAndRelationshipsFromValue(item, addNode, addRelationship);
    });
  }
};

export const applyGraphTypes = (
  item: BoltRecord['_fields'],
): Node | Relationship | Integer | PathSegment | Path | RecordShape<string, unknown> | BoltRecord['_fields'] => {
  if (item === null || item === undefined) {
    return item;
  } else if (Array.isArray(item)) {
    return item.map(applyGraphTypes);
  } else if (
    item.hasOwnProperty('labels') &&
    item.hasOwnProperty('properties') &&
    (item.hasOwnProperty('identity') || item.hasOwnProperty('elementId'))
  ) {
    return new neo4j.types.Node(
      applyGraphTypes(item.identity),
      item.labels,
      applyGraphTypes(item.properties),
      item.elementId,
    );
  } else if (
    item.hasOwnProperty('labels') &&
    item.hasOwnProperty('propKeys') &&
    item.hasOwnProperty('propValues') &&
    (item.hasOwnProperty('identity') || item.hasOwnProperty('elementId'))
  ) {
    return new neo4j.types.Node(
      applyGraphTypes(item.identity),
      item.labels,
      applyGraphTypes(zipObject(item.propKeys, item.propValues)),
      item.elementId,
    );
  } else if (item.hasOwnProperty('segments') && item.hasOwnProperty('start') && item.hasOwnProperty('end')) {
    const start = applyGraphTypes(item.start) as Node;
    const end = applyGraphTypes(item.end) as Node;
    const segments = item.segments.map(applyGraphTypes);
    return new neo4j.types.Path(start, end, segments);
  } else if (item.hasOwnProperty('relationship') && item.hasOwnProperty('start') && item.hasOwnProperty('end')) {
    const start = applyGraphTypes(item.start) as Node;
    const end = applyGraphTypes(item.end) as Node;
    const relationship = applyGraphTypes(item.relationship) as Relationship;
    return new neo4j.types.PathSegment(start, relationship, end);
  } else if (
    (item.hasOwnProperty('identity') || item.hasOwnProperty('elementId')) &&
    (item.hasOwnProperty('start') || item.hasOwnProperty('startNodeElementId')) &&
    (item.hasOwnProperty('end') || item.hasOwnProperty('endNodeElementId')) &&
    item.hasOwnProperty('type')
  ) {
    return new neo4j.types.Relationship(
      applyGraphTypes(item.identity),
      applyGraphTypes(item.start),
      applyGraphTypes(item.end),
      item.type,
      applyGraphTypes(item.properties),
      item.elementId,
      item.startNodeElementId,
      item.endNodeElementId,
    );
  } else if (
    item.hasOwnProperty('low') &&
    item.hasOwnProperty('high') &&
    typeof item.low === 'number' &&
    typeof item.high === 'number'
  ) {
    return neo4j.int(item);
  } else if (typeof item === 'object') {
    const typedObject: RecordShape<string, unknown> = {};
    Object.keys(item).forEach((key) => {
      typedObject[key] = applyGraphTypes(item[key]);
    });
    return typedObject;
  }
  return item;
};

export const recordMapper = (typedRecord: Record) => {
  // @ts-expect-error we rely on internals here
  if (typedRecord._fields != null) {
    // @ts-expect-error and here
    typedRecord._fields = typedRecord._fields.map((field) => applyGraphTypes(field));
  }
  return typedRecord;
};
